import { ChangeDetectionStrategy, Component, output, signal, viewChild } from '@angular/core';

import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { ModalBaseModule } from 'Angular/features/components/modals/modal-base/modal-base.module';
import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';
import { IconSearchAutocompleteModule } from 'SearchApp/ts/features/icon-search-autocomplete/icon-search-autocomplete.module';

import { IconSearchAutocompleteComponent } from 'SearchApp/ts/features/icon-search-autocomplete/icon-search-autocomplete.component';

interface SmartCompareSelection {
    securityId: string;
    displaySecurityId: string;
    name: string;
}

export interface SmartCompareSubmit {
    securityIds: string[];
    promptText: string;
}

@Component({
    selector: 'ycn-agent-smart-compare-modal',
    imports: [IconSearchAutocompleteModule, ModalBaseModule, IconModule, TooltipModule],
    templateUrl: './agent-smart-compare-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSmartCompareModalComponent {
    submitCompare = output<SmartCompareSubmit>();

    dismiss = output<void>();

    private readonly _autocompleter = viewChild<IconSearchAutocompleteComponent>('autocompleter');

    protected readonly isOpen = signal(false);

    protected readonly items = signal<SmartCompareSelection[]>([]);

    protected readonly minItems = 2;

    protected readonly maxItems = 12;

    public open(prefilled: SmartCompareSelection[] = []): void {
        this.items.set(prefilled.slice(0, this.maxItems));
        this.isOpen.set(true);
        setTimeout(() => this._autocompleter()?.focus(), 0);
    }

    public close(): void {
        this.isOpen.set(false);
        this.items.set([]);
    }

    protected onAutocompleteSelect(result: AutocompleteResult): void {
        const current = this.items();
        if (current.length >= this.maxItems) {
            return;
        }
        if (current.some((i) => i.securityId === result.search_id)) {
            return;
        }
        this.items.set([
            ...current,
            {
                securityId: result.search_id,
                displaySecurityId: result.display_security_id,
                name: result.name,
            },
        ]);
    }

    protected removeItem(index: number): void {
        const next = [...this.items()];
        next.splice(index, 1);
        this.items.set(next);
    }

    protected canSubmit(): boolean {
        const { length } = this.items();
        return length >= this.minItems && length <= this.maxItems;
    }

    protected submit(): void {
        if (!this.canSubmit()) {
            return;
        }
        const items = this.items();
        const securityIds = items.map((i) => i.securityId);
        const displayIds = items.map((i) => i.displaySecurityId);
        const promptText =
            displayIds.length === 2
                ? `Smart Compare ${displayIds[0]} and ${displayIds[1]}`
                : `Smart Compare ${displayIds.slice(0, -1).join(', ')} and ${displayIds[displayIds.length - 1]}`;
        this.submitCompare.emit({ securityIds, promptText });
        this.close();
    }

    protected onDismiss(): void {
        this.close();
        this.dismiss.emit();
    }
}
